export interface ScopeLevel {
  key: string;
  name: string;
  description: string;
}

export interface ScopeGroup {
  key: string;
  defaultLevel: string;
  readOnly: boolean;
  levels: ScopeLevel[];
}

export type ScopeSelection = Record<string, string>;

export function defaultSelection(groups: ScopeGroup[]): ScopeSelection {
  return Object.fromEntries(groups.map((g) => [g.key, g.defaultLevel]));
}

// "none" is the absence of a grant, not a scope to send.
export function toScopeStrings(
  groups: ScopeGroup[],
  selection: ScopeSelection,
): string[] {
  return groups
    .map((g) => `${g.key}_${selection[g.key] ?? g.defaultLevel}`)
    .filter((scope) => !scope.endsWith("_none"));
}

export function grantedCount(
  groups: ScopeGroup[],
  selection: ScopeSelection,
): { granted: number; fullAccess: number } {
  let granted = 0;
  let fullAccess = 0;
  for (const group of groups) {
    const level = selection[group.key] ?? group.defaultLevel;
    if (level === "none") continue;
    granted += 1;
    if (level === "full_access") fullAccess += 1;
  }
  return { granted, fullAccess };
}

export function levelOf(group: ScopeGroup, selection: ScopeSelection): ScopeLevel {
  const key = selection[group.key] ?? group.defaultLevel;
  return group.levels.find((l) => l.key === key) ?? group.levels[0];
}

export function selectionFromScopes(
  groups: ScopeGroup[],
  scopes: string[],
): ScopeSelection {
  const granted = new Set(scopes);
  return Object.fromEntries(
    groups.map((group) => {
      const level = group.levels.find((l) =>
        granted.has(`${group.key}_${l.key}`),
      );
      return [group.key, level?.key ?? "none"];
    }),
  );
}
